import React, { ChangeEvent, useEffect } from 'react';

export interface IResponse {
  name: string;
  value: ICheckboxOptions[] | ICheckboxOptions | boolean | null;
  type?: string;
  id?: string | number;
}

export type TOptionValue = 'id' | 'name' | string | number | 'checked';

export interface ICheckboxOptions {
  [key: TOptionValue]: string | boolean;
}

export interface ICheckboxProps {
  options?: ICheckboxOptions[];
  action: (e: ICheckboxOptions | boolean, resp?: IResponse) => void;
  checked?: ICheckboxOptions[] | boolean;
  label?: string | JSX.Element;
  name: string;
  type?: string;
  color?: string;
  colorCustom?: string;
  text?: string;
  disabled?: boolean;
  disabledMessage?: string;
  optionLabel?: string;
  optionValue?: string;
  optionRight?: string[] | JSX.Element[];
  textRight?: string | JSX.Element;
  cy: string;
}

export const Checkbox: React.FC<ICheckboxProps> = ({
  options,
  action,
  checked,
  label,
  name,
  type = 'checkbox',
  color = '',
  colorCustom = '',
  text = '',
  disabled = false,
  disabledMessage,
  optionLabel = 'name',
  optionValue = 'id',
  optionRight = [],
  textRight = '',
  cy = '',
}: ICheckboxProps) => {
  const valueToReturn = (checkedItem: ICheckboxOptions | boolean) => {
    if (checked) {
      if (typeof checked === 'boolean') {
        return checked ? checkedItem : [];
      }
      if (typeof checkedItem === 'boolean') {
        return checkedItem;
      }
      const verify = checked.filter((elem) => {
        return elem[optionValue] === checkedItem[optionValue] ? elem : null;
      });

      const res = checked.filter((elem) => {
        return elem[optionValue] !== checkedItem[optionValue] ? elem : null;
      });

      if (checked.length === 0) {
        return [checkedItem];
      }
      if (verify.length === 0) {
        return checked.concat(checkedItem);
      }
      return res;
    }
    return null;
  };

  const checkedAction = (checkedItem: ICheckboxOptions | boolean) => {
    if (disabled) return;
    if (checkedItem !== undefined) {
      const resp = options
        ? checkedItem
          ? valueToReturn(checkedItem)
          : []
        : checkedItem;
      const fullResponse: IResponse = { name, value: resp, type, id: cy };
      if (!!action) {
        action(checkedItem as ICheckboxOptions, fullResponse);
      }
    }
  };

  const optionCheckedAction = (checkedItem: ICheckboxOptions) => {
    if (disabled) return;
    if (!!action) {
      action(checkedItem as ICheckboxOptions);
    }
  };

  return (
    <div
      className={`form-box form-checkbox ${type} ${color} ${
        disabled ? 'disabled' : ''
      }`}
    >
      {label ? (
        <label htmlFor={`id-${name}`} data-cy={`FormCheckbox${name}${cy}`}>
          {label}
        </label>
      ) : null}
      {disabledMessage && disabled && (
        <div
          className="checkboxDisabledMessage"
          data-cy={`FormCheckbox${name}${cy}`}
        >
          <span>{disabledMessage}</span>
        </div>
      )}
      {!!options ? (
        options.map((item, index) => {
          return (
            <div
              key={`checkbox-${name}-${index}`}
              className={`${name}-wrapper`}
            >
              <div
                key={`${name}-${item[optionValue]}`}
                className={`check-box ${item.checked ? 'checked' : ''}`}
                onClick={() => optionCheckedAction(item)}
                data-cy={`FormCheckbox${name}Optionclick${item[optionValue]}${cy}`}
              >
                <span
                  className={type}
                  style={
                    item.checked && (colorCustom || item.color)
                      ? {
                          backgroundColor:
                            colorCustom || (item.color as string),
                        }
                      : {}
                  }
                  data-cy={`FormCheckbox${name}Label${item[optionLabel]}`}
                ></span>
                {item[optionLabel]}
              </div>
              {item.right ? optionRight : null}{' '}
              {item.textRight ? textRight : null}
            </div>
          );
        })
      ) : (
        <div
          className={`check-box ${checked ? 'checked' : ''}`}
          onClick={() => checkedAction(!disabled ? !checked : false)}
          data-cy={`FormCheckbox${name}Optionclick${cy}`}
        >
          <span
            className={type}
            style={
              checked && colorCustom ? { backgroundColor: colorCustom } : {}
            }
            data-cy={`FormCheckbox${name}Label${cy}`}
          ></span>
          {text}
        </div>
      )}
    </div>
  );
};
